import PropTypes from 'prop-types';
import { Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import { useEffect, useRef } from 'react';
import UniversalContainer, {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from '../universal-container';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
} from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import styles from './awards-2024-buttons.module.less';

function getLinkUrl(linkUrl, shouldTrack, title) {
  const isAnchorLink = linkUrl.charAt(0) === '#';
  if (isAnchorLink || !shouldTrack) {
    return linkUrl;
  }

  const modifiedUrl = setTrk({
    url: linkUrl,
    params: {
      [TrkTag.location]: 'AwardsNavigation',
      [TrkTag.element]: TrkElement.hyperlink,
      [TrkTag.copy]: title?.toLowerCase(),
      [TrkTag.sectionCategory]: 'AwardsNavigation--header',
    },
  });

  return modifiedUrl;
}

const setStickyHeaderBehavior = (flexCardElement, stickyHeaderElement) => {
  if (!flexCardElement || !stickyHeaderElement) return;
  const stickyHeaderComponent = stickyHeaderElement;
  const elementHeight = 120;

  if (flexCardElement.getBoundingClientRect().top < -elementHeight) {
    stickyHeaderComponent.style.position = 'fixed';
    stickyHeaderComponent.style.top = 0;
  } else {
    stickyHeaderComponent.style.top = '-50%';
  }
};

const Awards2024ButtonsBox = ({
  boxIconUrl,
  boxTitle,
  boxLinkUrl,
  shouldTrack,
}) => {
  const url = getLinkUrl(boxLinkUrl, shouldTrack, boxTitle);

  return (
    <Link href={url} className={styles.boxLink} data-testid="button-box-link">
      <div className={styles.boxItem}>
        {boxIconUrl && (
          <Image
            className={styles.boxIcon}
            src={boxIconUrl}
            alt="box-icon"
            backgroundColor="transparent"
          />
        )}
        <div className={styles.boxTitle}>
          <Text>{boxTitle}</Text>
        </div>
      </div>
    </Link>
  );
};

Awards2024ButtonsBox.propTypes = {
  children: PropTypes.node,
  boxIconUrl: PropTypes.string,
  boxTitle: PropTypes.string,
  boxLinkUrl: PropTypes.string,
  shouldTrack: PropTypes.bool,
};

Awards2024ButtonsBox.defaultProps = {
  children: null,
  boxIconUrl: '',
  boxTitle: '',
  boxLinkUrl: '',
  shouldTrack: false,
};

const Awards2024Buttons = ({
  container,
  title,
  buttons,
  shouldTrack,
  disableStickyHeaderIntegration,
}) => {
  const awardsNavigationRef = useRef();

  useEffect(() => {
    const stickyHeader = document.getElementById('sticky-header-v2');
    if (stickyHeader) {
      stickyHeader.style.position = 'fixed';
      stickyHeader.style.display = 'flex';
      stickyHeader.style.top = '-50%';
    }
    const handleScroll = () => {
      setStickyHeaderBehavior(awardsNavigationRef.current, stickyHeader);
    };

    if (!disableStickyHeaderIntegration && stickyHeader) {
      window.addEventListener('scroll', handleScroll);
    } else if (stickyHeader) {
      stickyHeader.style.position = 'sticky';
      stickyHeader.style.top = '0';
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [disableStickyHeaderIntegration]);

  return (
    <UniversalContainer
      {...container}
      data-testid="awards2024-buttons-container"
    >
      <UniversalText {...title} />
      <div className={styles.boxItemsContainer} ref={awardsNavigationRef}>
        {buttons?.map((box) => (
          <Awards2024ButtonsBox
            key={box.$unique_key}
            boxIconUrl={box.boxIconUrl}
            boxLinkUrl={box.boxLinkUrl}
            boxTitle={box.boxTitle}
            shouldTrack={shouldTrack}
          />
        ))}
      </div>
    </UniversalContainer>
  );
};

Awards2024Buttons.displayName = 'Awards2024Buttons';

Awards2024Buttons.propTypes = {
  children: PropTypes.node,
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  buttons: PropTypes.array,
  shouldTrack: PropTypes.bool,
  disableStickyHeaderIntegration: PropTypes.bool,
};

Awards2024Buttons.defaultProps = {
  children: null,
  container: universalContainerDefaultProps,
  title: 'Awards2024Buttons',
  buttons: [],
  shouldTrack: false,
  disableStickyHeaderIntegration: false,
};

Awards2024Buttons.getEditingConfig = (componentDocument) => {
  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      ['padding', 'margin', 'backgroundColor', 'backgroundImage', 'jumpLinkId'],
      `container`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'padding',
        'textChronicle',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    boolCheckboxSpec(
      'shouldTrack',
      'Add BOA Awards Analytics Instrumentation?'
    ),
    boolCheckboxSpec(
      'disableStickyHeaderIntegration',
      'Disable StickyHeader Integration when Scrolling?'
    ),
    repeatable('buttons', 'Buttons', 'Button', () => [
      stringInputSpec('boxTitle', 'Button Title'),
      stringInputSpec('boxIconUrl', 'Button Icon URL'),
      stringInputSpec('boxLinkUrl', 'Button Link URL'),
    ]),
  ];
};
export default Awards2024Buttons;
